/**
 * Single source of truth for application status styling.
 *
 * Nine components had their own copy of this map, drifting apart over time.
 * Import from here instead, so a status added in the backend needs one edit.
 *
 * The old map used eight hues. This one uses five roles, because the color
 * should say what to do about a status, not just which status it is:
 *
 *   neutral   nothing to do right now
 *   active    needs your attention
 *   positive  you got the job
 *   negative  you did not
 *   waiting   paused, may resume
 *   closed    the role went away, which is not a rejection
 */

export const STATUS_ROLES = {
  Applied: "neutral",
  Screening: "neutral",
  Interview: "neutral",
  "Technical test": "neutral",
  "Final interview": "active",
  "Offer received": "active",
  Accepted: "positive",
  Rejected: "negative",
  Withdrawn: "neutral",
  // Bulk push statuses, set by the employer or agency rather than the seeker
  "Position filled": "closed",
  "Applications closed": "closed",
  "Process on hold": "waiting",
  "Not moving forward": "negative",
};

// Dark variants use a low-opacity tint of the same hue rather than a fixed
// dark shade, so pills sit on any surface without banding.
export const ROLE_CLASSES = {
  neutral: "bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-400",
  active:
    "bg-brand-violet-tint text-brand-violet dark:bg-brand-violet/20 dark:text-brand-violet-on-dark",
  positive:
    "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300",
  negative: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300",
  waiting: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  closed: "bg-slate-200 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300",
};

/** Classes for a status. Unknown statuses fall back to neutral rather than
 *  rendering unstyled, since the backend can add statuses ahead of the UI. */
export function statusClasses(status) {
  return ROLE_CLASSES[STATUS_ROLES[status] ?? "neutral"];
}
